import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter, Link } from "react-router-dom";
import styled from "styled-components";

import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Grid from "@mui/material/Grid";
import { default as Box } from "@mui/material/Container";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import StimService from "../../Stims/StimService";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import StudyTabNavigation from "../../Overview/containers/StudyTabNavigation";

import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";

const Container = styled.div``;

const colors = [
  "#42a5f5",
  "#66bb6a",
  "#ffa726",
  "#7e57c2",
  "#26a69a",
  "#ffca28",
  "#ff7043",
  "#5c6bc0",
  "#ab47bc",
  "#26c6da",
  "#ffee58",
  "#ec407a",
  "#29b6f6",
  "#d4e157",
  "#ef5350",
];

const STEP_TYPE_COLORS = {
  stim: "#fb8c00",
  control: "#1e88e5",
  question: "#43a047",
  default: "#9e9e9e",
};

class StudySpecificStimSelector extends Component {
  constructor(props) {
    super(props);
    this.state = {
      studyId: "",
      loading: true,
      density: "standard",
      searchTerm: "",
      studyName: "",
      cells: [],
      updatingImportant: {},

    };

    this.updateDensity = this.updateDensity.bind(this);
  }

  componentDidMount() {
    const studyId =
      this.props.match?.params?.studyId ||
      this.props.match?.params?.id ||
      localStorage.getItem("studyId") ||
      "";

    this.setState({ studyId });

    if (studyId) {
      localStorage.setItem("studyId", studyId);
      this.fetchStudySpecificStims(studyId);
    } else {
      this.setState({ loading: false });
    }
  }

  fetchStudySpecificStims = async (studyId) => {
    try {
      this.setState({ loading: true });

      const res = await StimService.getStimData(studyId);
      const payload = res?.data || {};

      this.setState({
        studyName: payload.study_name || "",

        cells: payload.data || [],
        loading: false,
      });
    } catch (error) {
      console.error("Error fetching study specific stims:", error);
      this.setState({
        cells: [],
        loading: false,
      });
    }
  };

  updateDensity(event, value) {
    if (!value) return;
    this.setState({ density: value });
  }

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

getImportantKey = (cellId, steporderId, stimId) => {
  return `${cellId}-${steporderId}-${stimId}`;
};

isStimImportantEditable = (step) => {
  const rawType =
    step?.step_category ||
    step?.step_type ||
    step?.type ||
    step?.category ||
    "";

  const normalizedType = String(rawType).toLowerCase();

  if (normalizedType.includes("stim")) {
    return false;
  }

  // fallback: real stimulus steps in your backend use cachepage_id = -1
  if (step?.cachepage_id === -1) {
    return false;
  }

  return true;
};

renderImportantToggle = (cell, step, stim) => {
  const importantKey = this.getImportantKey(
    cell.cell_id,
    step.steporder_id,
    stim.stimulus_id
  );

  const isSaving = !!this.state.updatingImportant[importantKey];
  const isEditable = this.isStimImportantEditable(step);
  const isStimStep = !isEditable;
  const isDisabled = isStimStep || isSaving;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 2,
        opacity: isDisabled ? 0.55 : 1,
      }}
    >
      <FormControlLabel
        style={{ margin: 0 }}
        control={
          <Switch
            size="small"
            checked={isStimStep ? true : !!stim.important}
            disabled={isDisabled}
            onChange={(e) =>
              this.handleImportantToggle(
                cell.cell_id,
                step,
                stim,
                e.target.checked
              )
            }
            sx={{
              "& .MuiSwitch-switchBase.Mui-disabled": {
                color: "#bdbdbd",
              },
              "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                backgroundColor: "#d9d9d9",
                opacity: 1,
              },
            }}
          />
        }
        label={
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ color: isDisabled ? "#9e9e9e" : undefined }}
          >
            {isStimStep ? "Always important" : "Important"}
          </Typography>
        }
      />

      {isSaving && <CircularProgress size={14} />}
    </div>
  );
};

  handleImportantToggle = async (cellId, step, stim, checked) => {
  const { studyId } = this.state;
  const steporderId = step.steporder_id;
  const stimId = stim.stimulus_id;
  const key = this.getImportantKey(cellId, steporderId, stimId);

  const previousCells = this.state.cells;

  // optimistic UI update
  const updatedCells = previousCells.map((cell) => {
    if (cell.cell_id !== cellId) return cell;

    return {
      ...cell,
      step_list: (cell.step_list || []).map((currentStep) => {
        if (currentStep.steporder_id !== steporderId) return currentStep;

        return {
          ...currentStep,
          stimulus_list: (currentStep.stimulus_list || []).map((currentStim) =>
            currentStim.stimulus_id === stimId
              ? { ...currentStim, important: checked }
              : currentStim
          ),
        };
      }),
    };
  });

  this.setState((prevState) => ({
    cells: updatedCells,
    updatingImportant: {
      ...prevState.updatingImportant,
      [key]: true,
    },
  }));

  try {
    await StimService.updateStudyStimImportant(
      studyId,
      cellId,
      steporderId,
      stimId,
      checked
    );
  } catch (error) {
    console.error("Error updating important flag:", error);

    // rollback on error
    this.setState({ cells: previousCells });
  } finally {
    this.setState((prevState) => ({
      updatingImportant: {
        ...prevState.updatingImportant,
        [key]: false,
      },
    }));
  }
};

  getStepTypeColor = (step) => {
    const rawType =
      step?.step_category ||
      step?.step_type ||
      step?.type ||
      step?.category ||
      "";

    const normalizedType = String(rawType).toLowerCase();

    if (normalizedType.includes("stim")) {
      return STEP_TYPE_COLORS.stim;
    }

    if (normalizedType.includes("control")) {
      return STEP_TYPE_COLORS.control;
    }

    if (normalizedType.includes("question")) {
      return STEP_TYPE_COLORS.question;
    }

    // fallback in case backend does not send type but stim steps contain stimuli
    if ((step?.stimulus_list || []).length > 0) {
      return STEP_TYPE_COLORS.stim;
    }

    return STEP_TYPE_COLORS.default;
  };

  filterCells = () => {
    const { cells, searchTerm } = this.state;
    const term = (searchTerm || "").trim().toLowerCase();

    if (!term) return cells;

    return (cells || [])
      .map((cell) => {
        const cellMatches = (cell.cell_name || "").toLowerCase().includes(term);

        const filteredSteps = (cell.step_list || [])
          .map((step) => {
            const stepMatches =
              String(step.step_order || "").includes(term) ||
              String(step.step_id || "").includes(term) ||
              (step.cachepage_name || "").toLowerCase().includes(term);

            const filteredStimuli = (step.stimulus_list || []).filter((stim) => {
              return (
                (stim.stimulus_name || "").toLowerCase().includes(term) ||
                (stim.stimulus_type || "").toLowerCase().includes(term) ||
                String(stim.stimulus_id || "").includes(term)
              );
            });

            if (cellMatches || stepMatches || filteredStimuli.length > 0) {
              return {
                ...step,
                stimulus_list:
                  cellMatches || stepMatches
                    ? step.stimulus_list || []
                    : filteredStimuli,
              };
            }

            return null;
          })
          .filter(Boolean);

        if (cellMatches || filteredSteps.length > 0) {
          return {
            ...cell,
            step_list: cellMatches ? cell.step_list || [] : filteredSteps,
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  renderStimPreview = (stim) => {
    const source =
      stim.stimulus_source ||
      stim.stimulus_source_alternate ||
      stim.stimulus_source_high_quality;

    if (!source) {
      return (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 6,
            background: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            color: "#777",
            flexShrink: 0,
          }}
        >
          No preview
        </div>
      );
    }

    if (stim.stimulus_type === "video") {
      return (
        <video
          style={{
            width: 56,
            height: 56,
            objectFit: "cover",
            borderRadius: 6,
            background: "#000",
            flexShrink: 0,
          }}
          muted
          playsInline
        >
          <source src={source} type="video/mp4" />
        </video>
      );
    }

    return (
      <img
        src={source}
        alt={stim.stimulus_name || "Stim preview"}
        style={{
          width: 56,
          height: 56,
          objectFit: "cover",
          borderRadius: 6,
          background: "#f5f5f5",
          flexShrink: 0,
        }}
      />
    );
  };

  render() {
    const {
      loading,
      density,
      searchTerm,
      studyName,
      studyId,
    } = this.state;

    const filteredCells = this.filterCells();

    if (!studyId && !loading) {
      return (
        <Paper elevation={0} style={{ borderRadius: 16, padding: 16 }}>
          <Typography variant="body2" color="textSecondary">
            Missing study id.
          </Typography>
        </Paper>
      );
    }

    if (loading) {
      return <LoadingSpinner />;
    }

    return (
     <Box style={{ padding: "0px" }} maxWidth="xl">
  <StudyTabNavigation />

  <div
    style={{
      position: "fixed",
      top: 5,
      right: 90,
      zIndex: 3000,
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: "white",
      borderRadius: 8,
      padding: "4px 8px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    }}
  >
    <ToggleButtonGroup
      style={{ background: "white" }}
      color="primary"
      value={density}
      exclusive
      onChange={this.updateDensity}
    >
      <ToggleButton
        style={{
          fontWeight: 500,
          fontSize: "0.875rem",
          lineHeight: 1.75,
          letterSpacing: "0.02857em",
          textTransform: "uppercase",
          borderRadius: "4px",
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          padding: "8px 12px",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          color: "rgba(0, 0, 0, 0.54)",
        }}
        value="standard"
      >
        Standard
      </ToggleButton>

      <ToggleButton
        style={{
          fontWeight: 500,
          fontSize: "0.875rem",
          lineHeight: 1.75,
          letterSpacing: "0.02857em",
          textTransform: "uppercase",
          borderRadius: "4px",
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          padding: "8px 12px",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          color: "rgba(0, 0, 0, 0.54)",
        }}
        value="dense"
      >
        Dense
      </ToggleButton>
    </ToggleButtonGroup>
  </div>

        <div
          style={{
            zIndex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            padding: "8px 8px 16px 8px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Typography variant="h5">
              {studyName || "Study Specific Stims"}
            </Typography>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              value={searchTerm}
              onChange={this.handleSearchChange}
              label="Search cells, steps, stims..."
              variant="outlined"
              style={{ minWidth: 260, background: "white" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>

        <Grid
          container
          style={{
            margin: "8px 0px",
            padding: "4px 8px 24px 8px",
            width: "100%",
            height: "calc(100vh - 170px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Container
            style={{
              display: "flex",
              flexDirection: "row",
              gap: density === "dense" ? "16px" : "24px",
              flexWrap: "nowrap",
              alignItems: "flex-start",
              width: "max-content",
              minWidth: "100%",
              overflowX: "auto",
              overflowY: "visible",
              paddingBottom: 8,
            }}
          >
            {filteredCells.map((cell, cellIndex) => (
              <Paper
                key={`cell-${cell.cell_id}-${cellIndex}`}
                elevation={density === "dense" ? 2 : 3}
                style={{
                  width: density === "dense" ? "300px" : "380px",
                  minWidth: density === "dense" ? "300px" : "380px",
                  maxWidth: density === "dense" ? "300px" : "380px",
                  padding: density === "dense" ? 12 : 16,
                  borderRadius: 6,
                  borderTop:
                    density === "dense"
                      ? `6px solid ${colors[cellIndex % colors.length]}`
                      : `8px solid ${colors[cellIndex % colors.length]}`,
                  display: "flex",
                  flexDirection: "column",
                  flexShrink: 0,
                  alignSelf: "flex-start",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 12,
                    gap: 8,
                  }}
                >
                  <div>
                    <Tooltip title={cell.cell_name}>
                      <Typography
                        variant={density === "dense" ? "subtitle1" : "h6"}
                        component="div"
                      >
                        {cell.cell_name}
                      </Typography>
                    </Tooltip>
                  </div>

                  <Typography
                    noWrap
                    variant="subtitle2"
                    component="span"
                    style={{
                      display: "inline",
                      background: "rgba(85, 105, 255, 0.1)",
                      color: "rgb(85, 105, 255)",
                      maxHeight: "24px",
                      padding: "2px 8px",
                      borderRadius: "6px",
                    }}
                  >
                    <b>{(cell.step_list || []).length}</b>
                  </Typography>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {(cell.step_list || []).map((step, stepIndex) => {
                    const stepColor = this.getStepTypeColor(step);

                    return (
                      <Paper
                        key={`cell-${cell.cell_id}-step-${step.step_id}-${stepIndex}`}
                        elevation={0}
                        style={{
                          border: "1px solid rgba(0,0,0,0.12)",
                          borderRadius: 6,
                          padding: 12,
                          borderLeft: `4px solid ${stepColor}`,
                        }}
                      >
                        <div style={{ marginBottom: 10 }}>
                          <Typography variant="subtitle2">
                            Step {step.step_order}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            Final users: {step.step_final_user_count || 0}
                          </Typography>
                          {step.cachepage_name ? (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              Cache page: {step.cachepage_name}
                            </Typography>
                          ) : null}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          {(step.stimulus_list || []).map((stim, stimIndex) => (
                            <Paper
                              key={`cell-${cell.cell_id}-step-${step.step_id}-stim-${stim.stimulus_id}-${stimIndex}`}
                              elevation={0}
                              style={{
                                padding: 10,
                                borderRadius: 6,
                                border: "1px solid rgba(0,0,0,0.08)",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                {this.renderStimPreview(stim)}

                                <div
                                  style={{
                                    minWidth: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Tooltip
                                    title={
                                      stim.stimulus_name || `Stim ${stim.stimulus_id}`
                                    }
                                  >
                                    <Typography
                                      component={Link}
                                      to={{
                                        pathname: `/stims/edit/${stim.stimulus_id}`,
                                        state: {
                                          fromStudySpecificStimSelector: true,
                                          returnTo: this.props.location.pathname,
                                          studyId: studyId,
                                        },
                                      }}
                                      variant="subtitle2"
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        textDecoration: "underline",
                                        color: "inherit",
                                        cursor: "pointer",
                                        fontWeight: 600,
                                        display: "block",
                                      }}
                                    >
                                      {stim.stimulus_name || `Stim ${stim.stimulus_id}`}
                                    </Typography>
                                  </Tooltip>

                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {stim.stimulus_type}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {stim.stimulus_width} × {stim.stimulus_height}
                                  </Typography>

                                  {this.renderImportantToggle(cell, step, stim)}

                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Viewed: {stim.panelist_viewed_percent || 0}% •
                                    Viewable: {stim.panelist_viewable_percent || 0}% •
                                    Dwell: {stim.avg_stim_dwell_time || 0}
                                  </Typography>
                                </div>
                              </div>

                              <Tooltip title="Open AOI">
                                <Button
                                  component={Link}
                                  to={`/stims/aoi/${stim.stimulus_id}?type=stim`}
                                  variant="outlined"
                                  size="small"
                                  endIcon={<ChevronRightIcon />}
                                  style={{
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    minWidth: "80px",
                                  }}
                                >
                                  AOI
                                </Button>
                              </Tooltip>
                            </Paper>
                          ))}

                          {!(step.stimulus_list || []).length && (
                            <Typography variant="body2" color="textSecondary">
                              No stims in this step.
                            </Typography>
                          )}
                        </div>
                      </Paper>
                    );
                  })}

                  {!(cell.step_list || []).length && (
                    <Typography variant="body2" color="textSecondary">
                      No steps in this cell.
                    </Typography>
                  )}
                </div>
              </Paper>
            ))}

            {!filteredCells.length && (
              <Typography variant="body2" color="textSecondary">
                No study-specific stims found.
              </Typography>
            )}
          </Container>
        </Grid>
      </Box>
    );
  }
}

StudySpecificStimSelector.propTypes = {
  match: PropTypes.object,
};

export default connect(null, null)(withRouter(StudySpecificStimSelector));